import { useState } from 'react';
import { Button, Icon, Label } from 'semantic-ui-react';
import moment from 'moment';
import { t } from '../locales';

function CalendarView({ events, onEventClick }) {
    const [current, setCurrent] = useState(moment().startOf('month'));

    const prev = () => setCurrent(m => m.clone().subtract(1, 'month'));
    const next = () => setCurrent(m => m.clone().add(1, 'month'));
    const today = () => setCurrent(moment().startOf('month'));

    const startDay = current.clone().startOf('month').startOf('isoWeek');
    const endDay = current.clone().endOf('month').endOf('isoWeek');

    // Build map: 'YYYY-MM-DD' -> [event, ...]
    const byDay = {};
    for (const event of events) {
        const dt = event.next_instance?.date_time;
        if (!dt) continue;
        const key = moment(dt).format('YYYY-MM-DD');
        if (!byDay[key]) byDay[key] = [];
        byDay[key].push(event);
    }

    const weeks = [];
    const day = startDay.clone();
    while (day.isSameOrBefore(endDay, 'day')) {
        const week = [];
        for (let i = 0; i < 7; i++) {
            week.push(day.clone());
            day.add(1, 'day');
        }
        weeks.push(week);
    }

    const dayNames = [
        t('calendar.mon'), t('calendar.tue'), t('calendar.wed'),
        t('calendar.thu'), t('calendar.fri'), t('calendar.sat'),
        t('calendar.sun'),
    ];

    return (
        <div>
            {/* Nav row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <Button icon size="small" onClick={prev}><Icon name="chevron left" /></Button>
                <Button size="small" onClick={today}>{t('calendar.today')}</Button>
                <Button icon size="small" onClick={next}><Icon name="chevron right" /></Button>
                <span style={{ fontWeight: 700, fontSize: '1.1rem', marginLeft: '0.5rem' }}>
                    {current.format('MMMM YYYY')}
                </span>
            </div>

            {/* Day-of-week headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
                {dayNames.map(d => (
                    <div key={d} style={{ textAlign: 'center', fontWeight: 600, fontSize: '0.8rem', padding: '4px 0', color: '#888' }}>
                        {d}
                    </div>
                ))}
            </div>

            {/* Weeks */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                {weeks.flat().map(d => {
                    const key = d.format('YYYY-MM-DD');
                    const dayEvents = byDay[key] || [];
                    const isToday = d.isSame(moment(), 'day');
                    const inMonth = d.isSame(current, 'month');

                    return (
                        <div
                            key={key}
                            style={{
                                minHeight: 80,
                                border: isToday ? '2px solid var(--colour_menu_main)' : '1px solid #e5e5e5',
                                borderRadius: 6,
                                padding: '4px 6px',
                                backgroundColor: inMonth ? '#fff' : '#f9f9f9',
                                opacity: inMonth ? 1 : 0.5,
                            }}
                        >
                            <div style={{
                                fontSize: '0.8rem',
                                fontWeight: isToday ? 700 : 400,
                                color: isToday ? 'var(--colour_menu_main)' : '#333',
                                marginBottom: 4,
                            }}>
                                {d.format('D')}
                            </div>
                            {dayEvents.slice(0, 3).map(ev => (
                                <Label
                                    key={ev.id}
                                    size="mini"
                                    style={{
                                        display: 'block',
                                        marginBottom: 2,
                                        cursor: 'pointer',
                                        backgroundColor: 'var(--colour_menu_main)',
                                        color: '#fff',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '100%',
                                    }}
                                    onClick={() => onEventClick(ev)}
                                    title={ev.name}
                                >
                                    {ev.name}
                                </Label>
                            ))}
                            {dayEvents.length > 3 && (
                                <span style={{ fontSize: '0.7rem', color: '#888' }}>
                                    {t('calendar.moreCount', { count: dayEvents.length - 3 })}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default CalendarView;
